import { trackEvent } from '../services/analyticsService';

/**
 * Event categories for analytics
 */
export enum EventCategory {
  WHEEL = 'Wheel',
  USER = 'User',
  SPIN = 'Spin',
  WISHLIST = 'Wishlist',
  TASK = 'Task',
  SHARE = 'Share',
  NAVIGATION = 'Navigation',
  ERROR = 'Error',
}

/**
 * Track wheel creation event
 * @param wheelId ID of the created wheel
 * @param wheelName Name of the wheel
 */
export const trackWheelCreation = (wheelId: string, wheelName: string) => {
  trackEvent(EventCategory.WHEEL, 'Create', wheelName, undefined);
};

/**
 * Track wheel edit event
 * @param wheelId ID of the edited wheel
 * @param wheelName Name of the wheel
 */
export const trackWheelEdit = (wheelId: string, wheelName: string) => {
  trackEvent(EventCategory.WHEEL, 'Edit', wheelName, undefined);
};

/**
 * Track wheel spin event
 * @param wheelId ID of the spun wheel
 * @param wheelName Name of the wheel
 * @param result Spin result
 */
export const trackWheelSpin = (wheelId: string, wheelName: string, result: string) => {
  trackEvent(EventCategory.SPIN, 'Spin', `${wheelName}: ${result}`, undefined);
};

/**
 * Track wheel share event
 * @param wheelId ID of the shared wheel
 * @param shareMethod Method used to share (e.g., 'Link', 'Email', 'Social')
 */
export const trackWheelShare = (wheelId: string, shareMethod: string) => {
  trackEvent(EventCategory.SHARE, shareMethod, wheelId, undefined);
};

/**
 * Track user sign up event
 * @param method Sign up method (e.g., 'Email', 'Google', 'Facebook')
 */
export const trackSignUp = (method: string) => {
  trackEvent(EventCategory.USER, 'Sign Up', method, undefined);
};

/**
 * Track user sign in event
 * @param method Sign in method (e.g., 'Email', 'Google', 'Facebook')
 */
export const trackSignIn = (method: string) => {
  trackEvent(EventCategory.USER, 'Sign In', method, undefined);
};

/**
 * Track feature usage
 * @param feature Feature name
 * @param action Action performed
 */
export const trackFeatureUsage = (feature: string, action: string) => {
  trackEvent(EventCategory.NAVIGATION, action, feature, undefined);
};

/**
 * Track error occurrence
 * @param errorType Type of error
 * @param errorMessage Error message
 * @param fatal Whether the error was fatal
 */
export const trackError = (errorType: string, errorMessage: string, fatal = false) => {
  trackEvent(EventCategory.ERROR, errorType, errorMessage, fatal ? 1 : 0);
};

/**
 * Track ATM creation event
 * @param atmId ID of the created ATM
 * @param atmName Name of the ATM
 * @param options Additional options for the ATM
 */
export const trackAtmCreation = (
  atmId: string,
  atmName: string,
  options: {
    theme?: string;
    isPublic?: boolean;
    optionCount?: number;
    requiresPin?: boolean;
  } = {}
) => {
  trackEvent(EventCategory.WHEEL, 'ATM_Create', atmName, undefined);

  // Track additional properties as separate events
  if (options.theme) {
    trackEvent(EventCategory.WHEEL, 'ATM_Theme', options.theme, undefined);
  }
  if (options.isPublic !== undefined) {
    trackEvent(
      EventCategory.WHEEL,
      'ATM_Visibility',
      options.isPublic ? 'Public' : 'Private',
      undefined
    );
  }
};

/**
 * Track ATM transaction event
 * @param atmId ID of the ATM
 * @param transactionType Type of transaction
 * @param amount Transaction amount (if applicable)
 */
export const trackAtmTransaction = (atmId: string, transactionType: string, amount?: number) => {
  trackEvent(EventCategory.SPIN, 'ATM_Transaction', transactionType, amount);
};

/**
 * Track ATM share event
 * @param atmId ID of the shared ATM
 * @param shareAction Action performed ('create', 'copy', 'activate', 'deactivate', 'delete')
 * @param options Additional share options
 */
export const trackAtmShare = (
  atmId: string,
  shareAction: string,
  options?: {
    shareId?: string;
    hasAccessCode?: boolean;
    hasExpiration?: boolean;
    maxTransactions?: number;
    url?: string;
  }
) => {
  trackEvent(EventCategory.SHARE, `ATM_${shareAction}`, atmId, undefined);

  // Track additional properties as separate events
  if (options?.hasAccessCode !== undefined) {
    trackEvent(
      EventCategory.SHARE,
      'ATM_AccessCode',
      options.hasAccessCode ? 'Required' : 'None',
      undefined
    );
  }
  if (options?.hasExpiration !== undefined) {
    trackEvent(
      EventCategory.SHARE,
      'ATM_Expiration',
      options.hasExpiration ? 'Set' : 'Never',
      undefined
    );
  }
  if (options?.maxTransactions !== undefined) {
    trackEvent(EventCategory.SHARE, 'ATM_TransactionLimit', 'Set', options.maxTransactions);
  }
};
